import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewRef,
    ViewChild,
    signal,
    ViewEncapsulation,
    inject,
    computed,
    ChangeDetectionStrategy,
} from "@angular/core";
import { ModalService } from "../modal/modal.service";
import { Task } from "../../pojo/Task";
import { S25Util } from "../../util/s25-util";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { PopoverComponent } from "../s25-popover/popover.component";
import { AvailWeekly } from "../s25-avail-weekly/s25.avail.weekly.util";
import { EventService } from "../../services/event.service";
import { EventData, EventProfile } from "../../services/event.service";
import { FlsService } from "../../services/fls.service";
import { AccessLevels, Fls } from "../../pojo/Fls";
import { OlsService } from "../../services/ols.service";
import { TaskService } from "../../services/task/task.service";
import { TaskCompetingUtil } from "./task.competing.util";
import { Item } from "../../pojo/Item";
import { WaitlistStore } from "../../stores/waitlist.store";
import { WaitlistEntry } from "../../pojo/Waitlist";
import { UserprefService } from "../../services/userpref.service";
import { Proto } from "../../pojo/Proto";
import ISODateString = Proto.ISODateString;

export interface TaskEditI {
    itemName: string;
    itemTypeId: Task.Id;
    itemId: number;
    eventId: number;
    itemStateId: Task.State;
    assignedToId: number;
    isTodo: boolean;
    todoType: Task.Todo.Type;
    listModelBean: any;
    showClass?: boolean;
    taskBlocked?: boolean;
    workflowChained?: boolean;
    itemQuantity?: string | number;
    objectType?: number;
    objectId?: number;
    competes?: Task.SpaceCompete[];
    reservationId?: number | null;
    eventName?: string;
    itemCount?: number;
    waitlistEnabled?: boolean;
    waitlistEntries?: WaitlistEntry[];
}

@TypeManagerDecorator("s25-ng-task-edit")
@Component({
    selector: "s25-ng-task-edit",
    template: `
        <span class="ngBold ngInlineBlock {{ hasModal ? 'ngAnchor' : '' }}">
            <span (click)="click()" (keydown.enter)="click()" tabindex="0">
                @if (this.showIcon) {
                    <span>
                        @if (taskModel.itemStateId === 1) {
                            <s25-ng-icon [type]="'taskDefault'" [ariaLabel]="'taskDefault'"></s25-ng-icon>
                        } @else if (taskModel.itemStateId === 3 || taskModel.itemStateId === 99) {
                            <s25-ng-icon [type]="'taskCancelled'" [ariaLabel]="'taskCancelled'"></s25-ng-icon>
                        } @else if (taskModel.itemStateId === 2) {
                            <s25-ng-icon [type]="'taskComplete'" [ariaLabel]="'taskComplete'"></s25-ng-icon>
                        }
                    </span>
                }

                {{ taskModel.itemName }}
                @if (taskModel.objectType === 6 && taskModel.itemQuantity) {
                    <span class="resource-quantity">{{ " [Quantity: " + taskModel.itemQuantity + "]" }}</span>
                }
            </span>
        </span>

        @if (taskModel?.competes?.length > 0 && waitlistEnabled()) {
            <span class="waitlist-icon ngBold ngInlineBlock" (focusin)="prefetchWaitlistOnFocus()">
                <s25-popover
                    [popoverTemplate]="popoverContent"
                    [openTrigger]="'click'"
                    [closeTrigger]="'click'"
                    (mouseenter)="onPopoverTrigger()"
                >
                    <s25-ng-icon-button [type]="'queue'" [ariaLabel]="'Waitlist Order'"></s25-ng-icon-button
                    >{{ totalWaitingRsrv() > 0 ? totalWaitingRsrv() : competes?.length }}
                </s25-popover>
                <ng-template #popoverContent>
                    <span (mouseleave)="onPopoverMouseLeave()" (mouseenter)="onPopoverMouseEnter()">
                        <div class="waitlist-header ngBold">Waitlist Info</div>
                        @for (rsrv of rsrvConflicts; track rsrv.conflictRsrvId) {
                            <div class="rsrv-conflict">
                                <b class="spacer-order">Conflict: </b>
                                <s25-item
                                    [itemId]="rsrv.conflictId"
                                    [itemTypeId]="Item.Ids.Event"
                                    [includeTypeIcon]="true"
                                    [itemName]="rsrv.conflictName"
                                    [placement]="['right', 'right-top', 'right-bottom']"
                                />
                            </div>
                        }

                        <div class="rsrv-conflict"><b>Waitlist Order:</b></div>
                        @if (totalWaitingRsrv() > 0) {
                            @for (item of rsrvWaiting(); track "rsrv_" + i; let i = $index) {
                                <div class="c-margin-bottom--single">
                                    <b>{{ item.waiting[0].startDt | dateFormat: dateFormat }}</b>
                                    @for (waiting of item.waiting; track "waiting_" + +i; let i = $index) {
                                        <div class="content-wrapper">
                                            <b class="spacer-order">{{ waiting.position | ordinalSuffix }}</b>
                                            <s25-item
                                                [itemId]="waiting.eventId"
                                                [includeTypeIcon]="true"
                                                [itemTypeId]="Item.Ids.Event"
                                                [itemName]="waiting.eventName"
                                                [placement]="['right', 'right-top', 'right-bottom']"
                                            />
                                        </div>
                                    }
                                </div>
                            }
                        } @else {
                            @for (group of competesByDate; track group.date; let gi = $index) {
                                <div class="c-margin-bottom--single">
                                    @if (group.date) {
                                        <b>{{ group.date | dateFormat: dateFormat }}</b>
                                    }
                                    @for (item of group.items; track item.conflictRsrvId) {
                                        <div class="content-wrapper">
                                            <b class="spacer-order">{{
                                                getCompeteDisplayPosition(item, group.items) | ordinalSuffix
                                            }}</b>
                                            <s25-item
                                                [itemId]="$safeNavigationMigration(item?.conflictId)"
                                                [itemTypeId]="Item.Ids.Event"
                                                [includeTypeIcon]="true"
                                                [itemName]="$safeNavigationMigration(item?.conflictName)"
                                                [placement]="['right', 'right-top', 'right-bottom']"
                                            ></s25-item>
                                        </div>
                                    }
                                </div>
                            }
                        }

                        @if (goToObjectParams() && hasPerm()) {
                            <div class="go-to-link">
                                <a
                                    s25-ng-router-link
                                    [stateName]="'locationAvailabilityWeekly'"
                                    [stateParams]="goToObjectParams()"
                                    (click)="goToObject()"
                                    >Go To Location</a
                                >
                            </div>
                        }
                    </span>
                </ng-template>
            </span>
        }
    `,
    standalone: false,
    styles: `
        .waitlist-header {
            padding: 8px 15px;
            background-color: var(--color--surface-2-bg);
            position: relative;
            left: 0;
            right: 0;
            margin: -16px -16px 0 -16px;
        }
        .rsrv-conflict {
            padding: 4px 0px;
        }
        a {
            background-color: var(--color--surface-2-bg);
            padding: 6px 12px;
            border-radius: 3px;

            &:hover {
                background-color: var(--color--surface-2-bg-hover);
            }
        }

        .waitlist-icon {
            padding: 10px 0px;
            color: #c00 !important;

            ::ng-deep s25-ng-icon-button s25-ng-icon svg {
                color: #c00 !important;
                fill: #c00 !important;
                stroke: #c00 !important;
            }
        }

        .content-wrapper {
            display: flex;
            align-items: center;
            padding-left: 15px;
        }

        .spacer-order {
            min-width: 30px;
            text-align: right;
            padding-right: 5px;
            flex-shrink: 0;
        }

        .go-to-link {
            text-align: center;
            padding-top: 10px;
        }
    `,
    changeDetection: ChangeDetectionStrategy.Eager,
    encapsulation: ViewEncapsulation.Emulated,
})
export class S25TaskEditComponent implements OnInit {
    @ViewChild(PopoverComponent) popover: PopoverComponent;
    @Input() taskModel: TaskEditI;
    @Input() showIcon?: boolean = false;
    @Input() taskBlocked?: boolean = false;
    @Input() waitlistQueryKey?: Date;
    @Output() onRefresh = new EventEmitter();
    hasModal: boolean;
    taskIcon: string = "";
    competes: Task.SpaceCompete[] = [];
    rsrvConflicts: Task.SpaceCompete[] = [];
    competesByDate: { date: ISODateString | undefined; items: Task.SpaceCompete[] }[] = [];

    protected readonly Item = Item;

    private static openPopover: PopoverComponent | null = null;
    private timeoutId: ReturnType<typeof setTimeout> | null = null;

    goToObjectParams = signal<{
        itemTypeId: number;
        itemId: number;
        startDt: string;
        weeks: number;
    } | null>(null);

    hasPerm = signal<boolean>(false);

    waitlistEnabled = signal<boolean>(false);

    private waitlistStore = inject(WaitlistStore);

    /** eventId:rsrvId -> queue position from waitlist API */
    private waitlistPositionByEventRsrv = new Map<string, number>();

    /** eventId:rsrvId of the event holding the space (WaitlistEntry blocking), first in the popover. */
    private blockingEventRsrvKeys = new Set<string>();

    /** In-flight lazy fetch so repeated hover/focus awaits one request. */
    private waitlistPositionsLoadPromise: Promise<void> | null = null;

    rsrvWaiting = signal<WaitlistEntry[]>([]);

    readonly totalWaitingRsrv = computed(() =>
        (this.rsrvWaiting() ?? []).reduce((total, entry) => total + (entry.waiting?.length ?? 0), 0),
    );

    dateFormat: string;

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
    ) {
        this.elementRef.nativeElement.angBridge = this;
    }

    async ngOnInit() {
        if (this.taskModel?.objectType === Item.Ids.Location) {
            this.waitlistEnabled.set(
                this.taskModel.waitlistEnabled ?? (await TaskCompetingUtil.waitlistEnabled(this.taskModel?.objectId)),
            );
        }
        if ((this.taskModel?.competes?.length ?? 0) > 0 && this.waitlistEnabled()) {
            this.applyWaitlistPositionsForSort(this.taskModel?.waitlistEntries, true);
            // get competes data by eventId instead of by this task Id, cuz of grouping ANG-6697, get max comp
            if ((this.taskModel?.itemCount ?? 0) > 1) {
                const eventCompetesData = await TaskService.getMicroTasksByEventIds(
                    [this.taskModel.eventId],
                    ["competes"],
                );

                const allMaxCompetes = this.getMaxCompetes(eventCompetesData ?? []);
                this.rsrvConflicts = S25TaskEditComponent.uniqueByConflictId(
                    allMaxCompetes.filter((c) => c.conflictType === "rsrv"),
                );
                const maxCompetesData = allMaxCompetes.filter((c) => c.conflictType !== "rsrv");

                this.competes = [
                    ...maxCompetesData,
                    {
                        spaceId: this.taskModel?.objectId ?? 0,
                        conflictType: "req_rsrv",
                        conflictId: this.taskModel.eventId,
                        conflictRsrvId: this.taskModel.reservationId ?? 0,
                        conflictName: this.taskModel?.eventName ?? "",
                        conflictEvEndDt: maxCompetesData[0]?.conflictEvEndDt,
                        conflictEvStartDt: maxCompetesData[0]?.conflictEvStartDt,
                    },
                ];
            } else {
                this.rsrvConflicts = S25TaskEditComponent.uniqueByConflictId(
                    (this.taskModel?.competes ?? []).filter((c) => c.conflictType === "rsrv"),
                );
                const filteredCompetes = (this.taskModel?.competes ?? []).filter((c) => c.conflictType !== "rsrv");
                const firstCompete = filteredCompetes[0];
                this.competes = [
                    ...filteredCompetes,
                    {
                        spaceId: this.taskModel?.objectId ?? 0,
                        conflictType: "req_rsrv",
                        conflictId: this.taskModel.eventId,
                        conflictRsrvId: this.taskModel.reservationId ?? 0,
                        conflictName: this.taskModel?.eventName ?? "",
                        conflictEvEndDt: firstCompete?.conflictEvStartDt,
                        conflictEvStartDt: firstCompete?.conflictEvEndDt,
                    },
                ];
            }
            // get waitlist data
            if (this.taskModel.waitlistEntries) {
                this.rsrvWaiting.set(this.taskModel.waitlistEntries);
            }

            if (this.waitlistQueryKey) {
                // event task list view
                const overlappingWaitlists = computed(() => {
                    const date = new Date(this.waitlistQueryKey ?? new Date());
                    if (!date) return [];

                    return (this.rsrvWaiting() ?? []).filter((wl) =>
                        wl.waiting?.some((w) => {
                            const start = new Date(w.startDt);
                            const end = new Date(w.endDt);
                            return start <= date && date <= end;
                        }),
                    );
                });
                this.rsrvWaiting.set(overlappingWaitlists());
            } else {
                //////////////////////////////////
                const allCompetes = [...(this.competes ?? []), ...(this.taskModel?.competes ?? [])];

                const conflictDates = new Set(
                    allCompetes
                        .filter((c) => c.conflictEvStartDt)
                        .map((c) => new Date(c.conflictEvStartDt!).toISOString().slice(0, 10)),
                );

                const filteredRsrvWaiting = this.rsrvWaiting()
                    .map((wl) => {
                        const waiting = wl.waiting?.filter((w) =>
                            conflictDates.has(new Date(w.startDt).toISOString().slice(0, 10)),
                        );
                        return waiting?.length ? { ...wl, waiting } : null;
                    })
                    .filter((wl): wl is WaitlistEntry => wl != null);

                this.rsrvWaiting.set(filteredRsrvWaiting);
            }

            this.applyWaitlistPositionsForSort(this.rsrvWaiting(), false);
            // Waitlist queue positions for sort/detail come from getWaitlistEntriesByRsrvIds on hover — see loadWaitlistPositionsForPopover
            this.competes = this.sortCompetesByWaitlistOrder(this.competes);
            this.groupCompetesByDate();
        }

        const itemTypeId = S25Util.parseInt(this.taskModel.itemTypeId);

        //only these task types have modals (not v-cal to-dos (type 6)...) OR taslBlocked and workflowChained
        this.hasModal = [1, 2, 3, 4, 5].indexOf(itemTypeId) > -1 && !this.taskBlocked;

        if (this.taskModel.taskBlocked && this.taskModel.workflowChained) {
            this.hasModal = false;
        }

        const [dateFormat, timeFormat] = await Promise.all([
            UserprefService.getS25Dateformat(),
            UserprefService.getS25Timeformat(),
        ]);
        this.dateFormat = dateFormat;
        // this.timeFormat = timeFormat;

        this.cd && !(this.cd as ViewRef).destroyed && this.cd.detectChanges();
    }

    click() {
        if (this.hasModal) {
            ModalService.modal("create-edit-task", {
                taskType: S25Util.parseInt(this.taskModel.itemTypeId),
                taskId: this.taskModel.itemId,
                type: "edit",
                eventId: this.taskModel.eventId,
                taskActionsCell: this.taskModel,
                listModelBean: this.taskModel.listModelBean,
                taskBlocked: this.taskBlocked,
                objectType: this.taskModel?.objectType,
                objectId: this.taskModel?.objectId,
                editCompCallBack: this.editCompCallBack,
            });
        }
    }

    //need to call back and refresh the dataset
    editCompCallBack = () => {
        this.onRefresh.emit();
    };

    refresh() {
        this.cd.detectChanges();
    }

    async calculateGoToObjectParams() {
        const eventData: EventData = await EventService.getEventIncludeCachedLong(
            this.taskModel.eventId,
            ["reservations"],
            true,
            false,
        );

        const profiles: EventProfile[] = S25Util.array.forceArray(eventData?.events?.event?.profile);

        const matchedProfile = profiles.find((profile) =>
            S25Util.array
                .forceArray(profile?.reservation)
                .some((res) => res?.reservation_id === this.taskModel?.reservationId),
        );

        //If no matching profile, use the start/end date of the event
        if (!matchedProfile) {
            const firstStartDate = eventData?.events?.event?.start_date;
            const lastEndDate = eventData?.events?.event?.end_date;
            const getWeeks: number = S25Util.date.diffWeeks(firstStartDate, lastEndDate);
            const getDows = AvailWeekly.getDowsFromDates([new Date(firstStartDate)]);
            return {
                itemTypeId: 4,
                itemId: this.taskModel.objectId,
                startDt: S25Util.date.toS25ISODateStr(new Date(firstStartDate)),
                weeks: getWeeks,
                dows: getDows,
            };
        }

        const reservations = S25Util.array.forceArray(matchedProfile.reservation);
        const sortedReservations = reservations.sort(S25Util.shallowSortDates("event_start_dt", "event_end_dt"));

        const firstStartDate = sortedReservations[0]?.event_start_dt;
        const lastEndDate = sortedReservations[sortedReservations.length - 1]?.event_end_dt;

        const startDates = sortedReservations.map((item) => new Date(item.event_start_dt));
        const getWeeks: number = S25Util.date.diffWeeks(firstStartDate, lastEndDate);
        //TODO: get the dows and pass in as params
        // const getDows = AvailWeekly.getDowsFromDates(startDates);

        return {
            itemTypeId: 4,
            itemId: this.taskModel.objectId,
            startDt: S25Util.date.toS25ISODateStr(new Date(firstStartDate)),
            weeks: getWeeks,
        };
    }

    async goToObject() {
        this.popover.forceClose();
    }

    /** Keyboard / focus: load waitlist order without relying on mouseenter. */
    prefetchWaitlistOnFocus(): void {
        void this.loadWaitlistPositionsForPopover();
    }

    async onPopoverTrigger() {
        // Close any previously open popover
        if (S25TaskEditComponent.openPopover && S25TaskEditComponent.openPopover !== this.popover) {
            S25TaskEditComponent.openPopover.forceClose();
        }

        await this.loadWaitlistPositionsForPopover();

        const params = await this.calculateGoToObjectParams();

        //calculate the params for the goToObject
        this.goToObjectParams.set(params);

        // perm for go to object
        const [fls, ols] = await Promise.all([
            FlsService.getFls(),
            OlsService.getOls([this.taskModel.objectId!], 4, "edit"),
        ]);

        const hasFLSccess = fls?.SPACE_PERM === AccessLevels.Full;
        const hasOlsAccess = Array.isArray(ols) && ols.some((o) => o.access_level !== AccessLevels.None);

        this.hasPerm.set(hasFLSccess || hasOlsAccess);

        // Open this one
        this.popover.forceOpen();
        S25TaskEditComponent.openPopover = this.popover;
    }

    onPopoverMouseLeave() {
        // Delay close
        this.timeoutId = setTimeout(() => {
            this.popover.forceClose();
            if (S25TaskEditComponent.openPopover === this.popover) {
                S25TaskEditComponent.openPopover = null;
            }
        }, 60);
    }

    onPopoverMouseEnter() {
        // Cancel close if user re-enters popover
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
    }

    /**
     * Loads per-rsrv waitlist rows used for ordering labels in the popover. Deferred from ngOnInit — runs on hover/focus of the waitlist control.
     */
    private loadWaitlistPositionsForPopover(): Promise<void> {
        if ((this.taskModel?.competes?.length ?? 0) === 0 || !this.waitlistEnabled()) {
            return Promise.resolve();
        }
        if (this.waitlistPositionByEventRsrv.size > 0) {
            return Promise.resolve();
        }
        const rsrvIds = S25Util.array.unique(
            [...(this.competes ?? []), ...(this.taskModel?.competes ?? [])]
                .map((c) => c.conflictRsrvId)
                .filter((id: number | undefined): id is number => (id ?? 0) > 0),
        );
        if (!rsrvIds.length) {
            return Promise.resolve();
        }
        if (!this.waitlistPositionsLoadPromise) {
            this.waitlistPositionsLoadPromise = (async () => {
                try {
                    const fetched = await this.waitlistStore.getWaitlistEntriesByRsrvIds(
                        rsrvIds,
                        "all_competes",
                        false,
                    );
                    this.applyWaitlistPositionsForSort(fetched, false);
                    this.competes = this.sortCompetesByWaitlistOrder(this.competes);
                    this.groupCompetesByDate();
                    this.cd && !(this.cd as ViewRef).destroyed && this.cd.detectChanges();
                } finally {
                    this.waitlistPositionsLoadPromise = null;
                }
            })();
        }
        return this.waitlistPositionsLoadPromise;
    }

    private applyWaitlistPositionsForSort(entries: WaitlistEntry[] | undefined | null, clearFirst: boolean): void {
        if (clearFirst) {
            this.waitlistPositionByEventRsrv.clear();
            this.blockingEventRsrvKeys.clear();
        }
        if (!entries?.length) return;
        for (const wl of entries) {
            if (wl.blockingEventId > 0 && wl.blockingRsrvId > 0) {
                this.blockingEventRsrvKeys.add(`${wl.blockingEventId}:${wl.blockingRsrvId}`);
            }
            for (const w of wl.waiting ?? []) {
                this.waitlistPositionByEventRsrv.set(`${w.eventId}:${w.rsrvId}`, w.position);
            }
        }
    }

    /**
     * Event holding the space (waitlist `blocking*`), for sort + 1st label. Never the RSRV conflict line (`conflictType === "rsrv"`).
     */
    isBlockingCompeteItem(c: Task.SpaceCompete): boolean {
        if (c.conflictType === "rsrv") {
            return false;
        }
        if (!this.blockingEventRsrvKeys.size) {
            return false;
        }
        return this.blockingEventRsrvKeys.has(`${c.conflictId}:${c.conflictRsrvId}`);
    }

    getCompetePositionOrNull(c: Task.SpaceCompete): number | null {
        const key = `${c.conflictId}:${c.conflictRsrvId}`;
        return this.waitlistPositionByEventRsrv.has(key) ? (this.waitlistPositionByEventRsrv.get(key) as number) : null;
    }

    /**
     * 1st = event holding the location (non-rsrv blocking). Then 2nd, 3rd, … from waitlist `position` when a holder is also listed; otherwise `position` maps to 1st, 2nd, …
     * Rows with no `position` use order in the group.
     */
    getCompeteDisplayPosition(c: Task.SpaceCompete, groupItems: Task.SpaceCompete[]): number {
        const hasBlocking = groupItems.some((x) => this.isBlockingCompeteItem(x));
        if (this.isBlockingCompeteItem(c)) {
            return 1;
        }
        const p = this.getCompetePositionOrNull(c);
        if (p != null) {
            return hasBlocking ? p + 1 : p;
        }
        const i = groupItems.findIndex((x) => x.conflictRsrvId === c.conflictRsrvId && x.conflictId === c.conflictId);
        return i >= 0 ? i + 1 : 1;
    }

    private sortCompetesByWaitlistOrder(items: Task.SpaceCompete[]): Task.SpaceCompete[] {
        return [...items].sort((a, b) => {
            const blockA = this.isBlockingCompeteItem(a);
            const blockB = this.isBlockingCompeteItem(b);
            if (blockA !== blockB) {
                return blockA ? -1 : 1;
            }

            const pa = this.getCompetePositionOrNull(a);
            const pb = this.getCompetePositionOrNull(b);
            const hasA = pa != null;
            const hasB = pb != null;
            if (hasA && hasB) return (pa as number) - (pb as number);
            if (hasA) return -1;
            if (hasB) return 1;
            return (a.conflictRsrvId ?? 0) - (b.conflictRsrvId ?? 0);
        });
    }

    private groupCompetesByDate() {
        const dateMap = new Map<string, Task.SpaceCompete[]>();
        for (const item of this.competes) {
            const dateKey = item.conflictEvStartDt
                ? new Date(item.conflictEvStartDt).toISOString().slice(0, 10)
                : "unknown";
            if (!dateMap.has(dateKey)) dateMap.set(dateKey, []);
            dateMap.get(dateKey)!.push(item);
        }
        this.competesByDate = Array.from(dateMap.entries()).map(([, items]) => {
            const dateItems = this.sortCompetesByWaitlistOrder(items);
            return {
                date: dateItems[0].conflictEvStartDt,
                items: dateItems,
            };
        });
    }

    private static uniqueByConflictId(items: Task.SpaceCompete[]): Task.SpaceCompete[] {
        return [...new Map(items.map((item) => [item.conflictId, item])).values()];
    }

    getMaxCompetes(items: EventData[]): Task.SpaceCompete[] {
        if (!items.length) return [];

        // Make sure `spaceCompetes` is always an array, even if it's null or undefined
        const counts = items.map(
            (t) =>
                t.competes?.reduce(
                    (sum: number, c: { spaceCompetes: Task.SpaceCompete[] }) => sum + (c.spaceCompetes?.length ?? 0),
                    0,
                ) ?? 0,
        );

        const maxCount = Math.max(...counts);

        const maxTasks = items.filter((t, i) => counts[i] === maxCount);

        return maxTasks.flatMap(
            (t) => t.competes?.flatMap((c: { spaceCompetes: Task.SpaceCompete[] }) => c.spaceCompetes ?? []) ?? [],
        );
    }
}
